import { format as formatDate } from 'date-fns';
import { styled } from '@mui/material/styles';
import {
  createTableColumnsV2,
  TableColumns,
} from '@components/TableV2/helpers/createTableColumnsV2';
import { useI18n } from '@hooks/useI18n';
import { IScheduleHistoryRow } from '@schemas/opsPortal/types/monetaScheduleHistory';
import { missingValuePlaceholder } from '@utils/constants';
import { isMinorArrearsFormatting } from './isMinorArrearsFormatting';

const TableCell = styled('div')({
  padding: '12px',
  whiteSpace: 'pre-wrap',
});

const BulletListStyled = styled('ul')({
  paddingLeft: '1.25rem',
  paddingTop: '1rem',
});

const ArrearsCell = styled('div')<{ isPositive: boolean }>(
  ({ isPositive }) => ({
    alignItems: 'center',
    backgroundColor: isPositive ? '#f5cbcc' : '#f3f3f3',
    color: isPositive ? '#990102' : 'inherit',
    display: 'flex',
    height: '100%',
    paddingLeft: '0.3rem',
    width: '100%',
  }),
);

export const useGetContractPaymentScheduleHistoryV2Columns = () => {
  const { translate } = useI18n();

  const columns: TableColumns<IScheduleHistoryRow> = [
    {
      field: 'date',
      label: translate('OPS_PORTAL.LABEL.SYSTEM_DATE'),
      mapCustomJSX: ({ row }) => (
        <TableCell>
          {row.date
            ? formatDate(new Date(row.date), 'yyyy-MM-dd')
            : missingValuePlaceholder}
        </TableCell>
      ),
    },
    {
      field: 'schedule_version',
      label: translate('OPS_PORTAL.LABEL.SCHEDULE_IN_EFFECT'),
    },
    {
      field: 'events',
      label: translate('OPS_PORTAL.LABEL.EVENT_OR_EVENTS'),
      mapCustomJSX: ({ row }) => (
        <BulletListStyled>
          {row?.events?.map(({ quantity, text }, index) => (
            <li
              key={`${quantity}-${text}-${index}`}
            >{`${quantity} x ${text}`}</li>
          ))}
        </BulletListStyled>
      ),
      flexWidth: 3,
    },
    {
      field: 'total_instalment_due',
      label: translate('OPS_PORTAL.LABEL.TOTAL_INSTALMENTS_DUE'),
    },
    {
      field: 'total_instalment_paid',
      label: translate('OPS_PORTAL.LABEL.TOTAL_INSTALMENTS_PAID'),
    },
    {
      field: 'total_instalment_arrears',
      label: translate('OPS_PORTAL.LABEL.TOTAL_INSTALMENT_ARREARS'),
      mapCustomJSX: ({ row }) => (
        <ArrearsCell isPositive={Number(row?.total_instalment_arrears) > 0}>
          {isMinorArrearsFormatting(
            row?.total_instalment_arrears,
            row?.is_minor_arrears ?? false,
          )}
        </ArrearsCell>
      ),
    },
    {
      field: 'total_late_fees_due',
      label: translate('OPS_PORTAL.LABEL.TOTAL_LATE_FEES_DUE'),
    },
    {
      field: 'total_late_fees_paid',
      label: translate('OPS_PORTAL.LABEL.TOTAL_LATE_FEES_PAID'),
    },
    {
      field: 'total_late_fees_arrears',
      label: translate('OPS_PORTAL.LABEL.TOTAL_LATE_FEE_ARREARS'),
      mapCustomJSX: ({ row }) => (
        <ArrearsCell isPositive={Number(row?.total_late_fees_arrears) > 0}>
          {isMinorArrearsFormatting(
            row?.total_late_fees_arrears,
            row?.is_minor_arrears ?? false,
          )}
        </ArrearsCell>
      ),
    },
    {
      field: 'total_write_off',
      label: translate('OPS_PORTAL.LABEL.TOTAL_WRITTEN_OFF'),
    },
  ];

  return createTableColumnsV2({ columns });
};
